// src/pages/CuisinerSansGaspillerPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import Seo from '../components/Seo';

const CuisinerSansGaspillerPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [recipes, setRecipes] = useState(null);
  const [newIngredient, setNewIngredient] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [suggestedIngredients, setSuggestedIngredients] = useState([]);
  const [formData, setFormData] = useState({
    time: 30,
    mealType: '',
    peopleCount: 2,
    dietaryRestrictions: []
  });

  const mealTypes = ['petit-déjeuner', 'déjeuner', 'dîner', 'collation', 'dessert'];
  const dietaryOptions = ['Végétarien', 'Vegan', 'Sans gluten', 'Sans lactose', 'Paleo', 'Keto'];

  useEffect(() => {
    const fetchSuggestedIngredients = async () => {
      try {
        const response = await api.get('/api/common-ingredients');
        setSuggestedIngredients(response.data.ingredients || []);
      } catch (error) {
        console.error('Erreur:', error);
        setSuggestedIngredients([
          "Pain", "Pâtes", "Riz", "Pommes de terre", "Carottes", 
          "Oignons", "Ail", "Tomates", "Lait", "Œufs", 
          "Fromage", "Beurre", "Poulet", "Bœuf", "Poisson"
        ]);
      }
    };

    fetchSuggestedIngredients();
  }, []);

  const filteredSuggestions = searchTerm 
    ? suggestedIngredients.filter(ingredient => 
        ingredient.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !selectedIngredients.includes(ingredient)
      ).slice(0, 10)
    : [];

  const handleIngredientSelect = (ingredient) => {
    if (!selectedIngredients.includes(ingredient)) {
      setSelectedIngredients([...selectedIngredients, ingredient]);
      setSearchTerm('');
    }
  };

  const handleAddNewIngredient = () => {
    if (newIngredient.trim() && !selectedIngredients.includes(newIngredient.trim())) {
      setSelectedIngredients([...selectedIngredients, newIngredient.trim()]);
      setNewIngredient('');
    }
  };

  const handleRemoveIngredient = (ingredient) => {
    setSelectedIngredients(selectedIngredients.filter(item => item !== ingredient));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDietaryChange = (option) => {
    const updated = formData.dietaryRestrictions.includes(option)
      ? formData.dietaryRestrictions.filter(item => item !== option)
      : [...formData.dietaryRestrictions, option];
    
    setFormData({ ...formData, dietaryRestrictions: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedIngredients.length === 0) {
      alert('Veuillez sélectionner au moins un ingrédient');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await api.post('/api/generate-recipes-from-ingredients', {
        ingredients: selectedIngredients,
        dietaryRestrictions: formData.dietaryRestrictions,
        mealType: formData.mealType,
        time: formData.time,
        peopleCount: formData.peopleCount
      });

      setRecipes(response.data.recipes);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue lors de la génération des recettes.');
    } finally {
      setIsLoading(false);
    }
  };

  const saveRecipe = async (recipeId) => {
    try {
      await api.post('/api/save-recipe', {
        recipeId,
        fromAntiWaste: true,
        savedAt: new Date(),
        usedIngredients: selectedIngredients
      });
      
      alert('Recette sauvegardée avec succès!');
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue lors de la sauvegarde de la recette.');
    }
  };

  return (
    <>
      <Seo
        title="Cuisiner sans gaspiller"
        description="Réutilisez vos restes et trouvez des recettes anti-gaspillage personnalisées avec YAQAM."
        breadcrumb
      />
      <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2" style={{ color: '#2C3E50' }}>
          Cuisiner Sans Gaspiller ♻️
        </h1>
        <p className="text-lg" style={{ color: '#6c757d' }}>
          Transformez vos restes en délicieux repas et réduisez le gaspillage alimentaire
        </p>
      </div>
      
      <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-100">
        <h2 className="text-2xl font-semibold mb-6" style={{ color: '#2C3E50' }}>
          Quels ingrédients avez-vous ?
        </h2>
        
        <form onSubmit={handleSubmit}>
          {/* Recherche et ajout d'ingrédients */}
          <div className="mb-6">
            <div className="flex mb-2">
              <div className="relative flex-grow">
                <input
                  type="text"
                  placeholder="Rechercher un ingrédient..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-l-2xl focus:ring-2 focus:border-transparent"
                  style={{ accentColor: '#87A96B' }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                
                {filteredSuggestions.length > 0 && searchTerm && (
                  <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-2xl shadow-lg mt-1 max-h-60 overflow-y-auto">
                    {filteredSuggestions.map((ingredient) => (
                      <div
                        key={ingredient}
                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => handleIngredientSelect(ingredient)}
                      >
                        {ingredient}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <button
                type="button"
                className="text-white px-6 py-3 rounded-r-2xl font-medium hover:opacity-90 transition-all"
                style={{ backgroundColor: '#87A96B' }}
                onClick={() => handleIngredientSelect(searchTerm)}
              >
                Ajouter
              </button>
            </div>
            
            <div className="flex mb-2">
              <input
                type="text"
                placeholder="Ou entrez un ingrédient personnalisé..."
                className="flex-grow px-4 py-3 border border-gray-200 rounded-l-2xl focus:ring-2 focus:border-transparent"
                style={{ accentColor: '#87A96B' }}
                value={newIngredient}
                onChange={(e) => setNewIngredient(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddNewIngredient();
                  }
                }}
              />
              <button
                type="button"
                className="text-white px-6 py-3 rounded-r-2xl font-medium hover:opacity-90 transition-all"
                style={{ backgroundColor: '#E67E22' }}
                onClick={handleAddNewIngredient}
              >
                Ajouter
              </button>
            </div>
          </div>
          
          {/* Ingrédients sélectionnés */}
          <div className="mb-6">
            <h3 className="font-medium mb-3" style={{ color: '#2C3E50' }}>
              Ingrédients sélectionnés
            </h3>
            {selectedIngredients.length === 0 ? (
              <p className="italic" style={{ color: '#6c757d' }}>
                Aucun ingrédient sélectionné
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {selectedIngredients.map((ingredient) => (
                  <div
                    key={ingredient}
                    className="flex items-center bg-gray-100 px-4 py-2 rounded-full border border-gray-200"
                  >
                    <span className="mr-2" style={{ color: '#2C3E50' }}>{ingredient}</span>
                    <button
                      type="button"
                      className="hover:opacity-70 transition-all"
                      style={{ color: '#dc2626' }}
                      onClick={() => handleRemoveIngredient(ingredient)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Type de repas */}
            <div>
              <label className="block font-medium mb-2" style={{ color: '#2C3E50' }}>
                Type de repas
              </label>
              <select
                name="mealType"
                value={formData.mealType}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:ring-2 focus:border-transparent"
                style={{ accentColor: '#87A96B' }}
              >
                <option value="">Tous les types de repas</option>
                {mealTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Nombre de personnes */}
            <div>
              <label className="block font-medium mb-2" style={{ color: '#2C3E50' }}>
                Nombre de personnes: {formData.peopleCount}
              </label>
              <input
                type="range"
                name="peopleCount"
                min="1"
                max="10"
                value={formData.peopleCount}
                onChange={handleChange}
                className="w-full"
                style={{ accentColor: '#87A96B' }}
              />
              <div className="flex justify-between text-xs" style={{ color: '#6c757d' }}>
                <span>1</span>
                <span>10</span>
              </div>
            </div>
          </div>
          
          {/* Temps de préparation */}
          <div className="mb-6">
            <label className="block font-medium mb-2" style={{ color: '#2C3E50' }}>
              Temps de préparation maximum: {formData.time} minutes
            </label>
            <input
              type="range"
              name="time"
              min="10"
              max="120"
              step="5"
              value={formData.time}
              onChange={handleChange}
              className="w-full"
              style={{ accentColor: '#87A96B' }}
            />
            <div className="flex justify-between text-xs" style={{ color: '#6c757d' }}>
              <span>10 min</span>
              <span>120 min</span>
            </div>
          </div>
          
          {/* Restrictions alimentaires */}
          <div className="mb-6">
            <label className="block font-medium mb-2" style={{ color: '#2C3E50' }}>
              Restrictions alimentaires
            </label>
            <div className="flex flex-wrap gap-2">
              {dietaryOptions.map((option) => (
                <button
                  type="button"
                  key={option}
                  onClick={() => handleDietaryChange(option)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    formData.dietaryRestrictions.includes(option)
                      ? 'text-white'
                      : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                  style={formData.dietaryRestrictions.includes(option) 
                    ? { backgroundColor: '#87A96B' } 
                    : { color: '#2C3E50' }
                  }
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
          
          <button
            type="submit"
            disabled={isLoading || selectedIngredients.length === 0}
            className={`w-full py-4 rounded-2xl font-medium transition-all flex items-center justify-center ${
              selectedIngredients.length === 0
                ? 'bg-gray-300 cursor-not-allowed'
                : 'text-white hover:opacity-90'
            }`}
            style={selectedIngredients.length > 0 ? { backgroundColor: '#87A96B' } : { color: '#6c757d' }}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Génération en cours...
              </>
            ) : (
              `Générer des recettes avec ${selectedIngredients.length} ingrédient${selectedIngredients.length > 1 ? 's' : ''}`
            )}
          </button>
        </form>
      </div>
      
      {/* Liste des recettes générées */}
      {recipes && (
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <h2 className="text-2xl font-semibold mb-6" style={{ color: '#2C3E50' }}>
            {recipes.length > 0 
              ? `${recipes.length} recette${recipes.length > 1 ? 's' : ''} trouvée${recipes.length > 1 ? 's' : ''} avec vos ingrédients` 
              : 'Aucune recette trouvée avec cette combinaison d\'ingrédients'}
          </h2>
          
          {recipes.length > 0 ? (
            <div className="space-y-6">
              {recipes.map((recipe) => (
                <div key={recipe.id} className="border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-semibold" style={{ color: '#2C3E50' }}>
                      {recipe.title}
                    </h3>
                    <div className="flex items-center text-sm" style={{ color: '#6c757d' }}>
                      <span className="mr-4">⏱️ {recipe.prepTime} min</span>
                      <span>👥 {recipe.servings} pers.</span>
                    </div>
                  </div>
                  
                  <p className="mb-4" style={{ color: '#6c757d' }}>
                    {recipe.description}
                  </p>
                  
                  <div className="mb-4">
                    <h4 className="font-medium mb-2" style={{ color: '#2C3E50' }}>
                      Ingrédients utilisés
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {recipe.usedIngredients.map((ingredient) => (
                        <span
                          key={ingredient}
                          className="px-3 py-1 rounded-full text-sm font-medium text-white"
                          style={{ backgroundColor: '#87A96B' }}
                        >
                          {ingredient}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {recipe.additionalIngredients.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-medium mb-2" style={{ color: '#2C3E50' }}>
                        Ingrédients additionnels nécessaires
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {recipe.additionalIngredients.map((ingredient) => (
                          <span
                            key={ingredient}
                            className="bg-gray-100 px-3 py-1 rounded-full text-sm font-medium"
                            style={{ color: '#2C3E50' }}
                          >
                            {ingredient}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      className="px-6 py-3 text-white rounded-full font-medium hover:opacity-90 transition-all"
                      style={{ backgroundColor: '#87A96B' }}
                      onClick={() => saveRecipe(recipe.id)}
                    >
                      Sauvegarder
                    </button>
                    <button
                      className="px-6 py-3 text-white rounded-full font-medium hover:opacity-90 transition-all"
                      style={{ backgroundColor: '#E67E22' }}
                      onClick={() => navigate(`/recette/${recipe.id}`)}
                    >
                      Voir la recette
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="mb-4" style={{ color: '#6c757d' }}>
                Essayez avec d'autres ingrédients ou moins de restrictions
              </p>
              <button
                className="px-6 py-3 text-white rounded-full font-medium hover:opacity-90 transition-all"
                style={{ backgroundColor: '#87A96B' }}
                onClick={() => setRecipes(null)}
              >
                Modifier les critères
              </button>
            </div>
          )}
        </div>
      )}
      </div>
    </>
  );
};

export default CuisinerSansGaspillerPage;
